/**
 * Coverage Gap Analysis View
 *
 * Dashboard showing how well this org's policies cover the regulatory
 * universe. Displays a CFR × coverage heatmap with drill-down to the
 * requirement level — for each CFR section, you can see which atomic
 * requirements are covered, by which policy, and which are missing.
 */
import React, { useState, useEffect, useCallback } from 'react';
import InfoTooltip from '../../components/InfoTooltip';
import { ChartBarSquareIcon } from '@heroicons/react/24/outline';

const API = process.env.REACT_APP_API_URL;

const COVERAGE_CFG = {
  covered:        { label: 'Covered',        color: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  partial:        { label: 'Partial',        color: 'bg-amber-50 text-amber-700 border-amber-200',       dot: 'bg-amber-500' },
  missing:        { label: 'Missing',        color: 'bg-red-50 text-red-700 border-red-200',             dot: 'bg-red-500' },
  not_applicable: { label: 'Not applicable', color: 'bg-slate-50 text-slate-500 border-slate-200',       dot: 'bg-slate-300' },
};

export default function CoverageView({ onOpenPolicy }) {
  const [cells, setCells] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanMsg, setScanMsg] = useState('');
  const [latestRun, setLatestRun] = useState(null);
  const [coverageFilter, setCoverageFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selectedCell, setSelectedCell] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, rRes] = await Promise.all([
        fetch(`${API}/api/ppm/coverage`, { credentials: 'include' }),
        fetch(`${API}/api/ppm/coverage/runs/latest`, { credentials: 'include' }),
      ]);
      const cData = await cRes.json();
      const rData = await rRes.json();
      if (cData.success) {
        setCells(cData.cells || []);
        setSummary(cData.summary || {});
      }
      if (rData.success) setLatestRun(rData.run || null);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const triggerScan = async () => {
    setScanning(true); setScanMsg('');
    try {
      const res = await fetch(`${API}/api/ppm/coverage/scan`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      setScanMsg(data.success ? 'Scan started — refresh in a minute' : (data.message || 'Failed'));
      setTimeout(load, 30000);
    } catch { setScanMsg('Scan failed'); }
    setScanning(false);
  };

  const openDetail = async (cell) => {
    setSelectedCell(cell);
    setDetail(null);
    try {
      const res = await fetch(`${API}/api/ppm/coverage/${cell.id}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setDetail(data);
    } catch {}
  };

  const filtered = cells.filter(c => {
    if (coverageFilter && c.coverage !== coverageFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!c.cfr_section.toLowerCase().includes(q) && !c.mandate_title.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const total = cells.length;
  const overallScore = total > 0
    ? Math.round(cells.reduce((sum, c) => sum + (c.coverage_score || 0), 0) / total)
    : 0;

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-slate-800 tracking-tight">Requirement Gap Alert</h2>
              <InfoTooltip
                title="Requirement Gap Alert"
                width={380}
                content={
                  <>
                    Shows which regulatory requirements your library <span className="text-white font-medium">does not yet have a policy for</span>.
                    An auditor asking "show me your policy on X" and you having nothing is a finding —
                    this surfaces those gaps so you can plug them before they're cited.
                  </>
                }
              />
            </div>
            <p className="text-slate-400 text-xs mt-0.5">
              {total} regulations tracked · {summary.covered || 0} covered · {summary.partial || 0} partial · {summary.missing || 0} missing
            </p>
          </div>
          <button onClick={triggerScan} disabled={scanning}
            className="px-5 py-2.5 rounded-xl bg-slate-800 text-white text-sm font-semibold hover:bg-slate-900 disabled:opacity-50 transition-colors">
            {scanning ? 'Starting...' : 'Run Coverage Scan'}
          </button>
        </div>

        {scanMsg && (
          <div className="mb-4 p-3 rounded-xl bg-orange-50 border border-orange-200 text-orange-700 text-xs">{scanMsg}</div>
        )}

        {latestRun && latestRun.finished_at && (
          <div className="mb-4 text-xs text-slate-400">
            Last scan: {new Date(latestRun.started_at).toLocaleString()} ·
            {' '}{latestRun.mandates_analyzed} mandates ·
            {' '}{latestRun.gaps_found} full gaps ·
            {' '}{latestRun.partials_found} partials ·
            {' '}{latestRun.recommendations_created} recommendations created
          </div>
        )}

        {/* Coverage summary cards */}
        {!loading && total > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-4">
              <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">Overall score</div>
              <div className="text-2xl font-bold text-slate-800">{overallScore}%</div>
              <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400" style={{ width: `${overallScore}%` }} />
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-4">
              <div className="text-xs text-emerald-600 uppercase tracking-wide mb-1">Covered</div>
              <div className="text-2xl font-bold text-slate-800">{summary.covered || 0}</div>
              <div className="text-xs text-slate-400">of {total}</div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-4">
              <div className="text-xs text-amber-600 uppercase tracking-wide mb-1">Partial</div>
              <div className="text-2xl font-bold text-slate-800">{summary.partial || 0}</div>
              <div className="text-xs text-slate-400">need gap closure</div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-4">
              <div className="text-xs text-red-600 uppercase tracking-wide mb-1">Missing</div>
              <div className="text-2xl font-bold text-slate-800">{summary.missing || 0}</div>
              <div className="text-xs text-slate-400">no policy at all</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-2 mb-4 flex-wrap items-center">
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search CFR section or title..."
            className="flex-1 min-w-[200px] rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-800 focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
          <div className="flex gap-1 p-1 bg-slate-100/80 rounded-xl">
            {['', 'missing', 'partial', 'covered'].map(s => (
              <button key={s || 'all'} onClick={() => setCoverageFilter(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all capitalize ${
                  coverageFilter === s ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}>{s || 'all'}</button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="space-y-2">
            {[1,2,3].map(i => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-4 animate-pulse">
                <div className="h-4 bg-slate-100 rounded w-1/3" />
              </div>
            ))}
          </div>
        )}

        {!loading && total === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl flex items-center justify-center text-orange-600">
              <ChartBarSquareIcon className="w-8 h-8" strokeWidth={1.75} />
            </div>
            <h3 className="text-base font-semibold text-slate-700 mb-1">No coverage data yet</h3>
            <p className="text-sm text-slate-400 mb-4">Run a coverage scan to see which regulations are addressed by your policies.</p>
            <button onClick={triggerScan} disabled={scanning}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-600 to-amber-500 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity">
              Run Coverage Scan
            </button>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left py-3 px-5 font-semibold text-xs text-slate-500 uppercase tracking-wide w-40">CFR Section</th>
                  <th className="text-left py-3 px-5 font-semibold text-xs text-slate-500 uppercase tracking-wide">Regulation</th>
                  <th className="text-left py-3 px-5 font-semibold text-xs text-slate-500 uppercase tracking-wide w-32">Coverage</th>
                  <th className="text-left py-3 px-5 font-semibold text-xs text-slate-500 uppercase tracking-wide w-24">Score</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(c => {
                  const cfg = COVERAGE_CFG[c.coverage] || COVERAGE_CFG.not_applicable;
                  return (
                    <tr key={c.id} onClick={() => openDetail(c)}
                      className="hover:bg-slate-50/70 transition-colors cursor-pointer">
                      <td className="py-3 px-5">
                        <span className="font-mono text-xs text-orange-700 font-medium">{c.cfr_section}</span>
                      </td>
                      <td className="py-3 px-5 text-slate-700">
                        {c.mandate_title}
                        {c.gap_summary && (c.coverage === 'missing' || c.coverage === 'partial') && (
                          <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-1">{c.gap_summary}</div>
                        )}
                      </td>
                      <td className="py-3 px-5">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border ${cfg.color}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                          {cfg.label}
                        </span>
                      </td>
                      <td className="py-3 px-5">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-slate-700 w-8">{c.coverage_score}%</span>
                          <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden min-w-[60px]">
                            <div className={`h-full ${c.coverage === 'covered' ? 'bg-emerald-500' : c.coverage === 'partial' ? 'bg-amber-500' : 'bg-red-500'}`}
                              style={{ width: `${c.coverage_score}%` }} />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Detail modal */}
        {selectedCell && (
          <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4"
            onClick={() => { setSelectedCell(null); setDetail(null); }}>
            <div className="bg-white rounded-2xl border border-slate-200 max-w-3xl w-full max-h-[90vh] overflow-auto"
              onClick={e => e.stopPropagation()}>
              <div className="sticky top-0 bg-white border-b border-slate-200 p-5 flex items-start justify-between">
                <div>
                  <div className="font-mono text-xs text-orange-700 font-medium">{selectedCell.cfr_section}</div>
                  <h3 className="text-base font-bold text-slate-800 mt-0.5">{selectedCell.mandate_title}</h3>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border ${(COVERAGE_CFG[selectedCell.coverage] || COVERAGE_CFG.not_applicable).color}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${(COVERAGE_CFG[selectedCell.coverage] || COVERAGE_CFG.not_applicable).dot}`} />
                      {(COVERAGE_CFG[selectedCell.coverage] || COVERAGE_CFG.not_applicable).label}
                    </span>
                    <span className="text-xs text-slate-500">
                      {selectedCell.requirements_covered}/{selectedCell.requirements_total} requirements covered ({selectedCell.coverage_score}%)
                    </span>
                  </div>
                </div>
                <button onClick={() => { setSelectedCell(null); setDetail(null); }}
                  className="text-slate-400 hover:text-slate-600">✕</button>
              </div>
              <div className="p-5">
                {selectedCell.gap_summary && (
                  <div className="mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200">
                    <div className="text-[10px] font-semibold text-amber-700 uppercase tracking-wide mb-1">Gap summary</div>
                    <p className="text-sm text-slate-700">{selectedCell.gap_summary}</p>
                  </div>
                )}
                {selectedCell.suggested_policy_title && (
                  <div className="mb-4 p-3 rounded-xl bg-orange-50 border border-orange-200">
                    <div className="text-[10px] font-semibold text-orange-700 uppercase tracking-wide mb-1">Suggested new policy</div>
                    <p className="text-sm text-slate-700">"{selectedCell.suggested_policy_title}"</p>
                  </div>
                )}

                {!detail && <div className="text-sm text-slate-400">Loading requirement detail...</div>}
                {detail && detail.requirements && (
                  <div>
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Requirements</div>
                    <div className="space-y-2">
                      {detail.requirements.map(r => (
                        <div key={r.id} className={`rounded-xl border p-3 ${r.covered ? 'border-emerald-200 bg-emerald-50/30' : 'border-red-200 bg-red-50/30'}`}>
                          <div className="flex items-start gap-2">
                            <span className={`mt-0.5 w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-bold ${r.covered ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}>
                              {r.covered ? '✓' : '✕'}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-slate-700 leading-relaxed">{r.requirement_text}</p>
                              <div className="mt-1 flex items-center gap-2 flex-wrap">
                                <span className="text-[10px] text-slate-400 uppercase tracking-wide">{r.requirement_type}</span>
                                {r.covered && r.policy_number && (
                                  <button onClick={() => onOpenPolicy(r.covering_policy_id)}
                                    className="text-[11px] text-orange-700 hover:underline font-mono">
                                    {r.policy_number}
                                  </button>
                                )}
                              </div>
                              {r.covered && r.evidence_quote && (
                                <p className="mt-1 text-[11px] text-slate-600 bg-white rounded px-2 py-1 border border-slate-100 font-mono leading-relaxed">
                                  "{r.evidence_quote}"
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
